import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import styles from './CalendarView.module.scss';
import { VehicleReservationDto } from 'api/vehicleReservations/models/VehicleReservationDto';
import CalendarDayCell from './CalendarDayCell';
import CalendarSidePanel from './CalendarSidePanel';

export type CalendarMode = 'reservations' | 'requests' | 'combined';

type Props = {
    mode: CalendarMode;
    items: VehicleReservationDto[];
    // eslint-disable-next-line unused-imports/no-unused-vars
    isLoading: boolean;
    currentMonth: Dayjs;
    selectedDate: Dayjs | null;
    // eslint-disable-next-line unused-imports/no-unused-vars
    onSelectDate: (date: Dayjs | null) => void;
    hasWorkflow?: boolean;
};

const CalendarView: React.FC<Props> = ({ mode, items, isLoading, currentMonth, selectedDate, onSelectDate, hasWorkflow = true }) => {
    const { t } = useTranslation();

    const weekDays = useMemo(() => {
        const days: string[] = [];
        for (let i = 1; i <= 7; i++) {
            days.push(dayjs().day(i % 7).format('ddd'));
        }
        return days;
    }, []);

    const calendarDays = useMemo(() => {
        const startOfMonth = currentMonth.startOf('month');
        const endOfMonth = currentMonth.endOf('month');

        // Monday = 1, Sunday = 0. We want Monday as first day.
        let startDay = startOfMonth.day(); // 0=Sun, 1=Mon, ...
        startDay = startDay === 0 ? 6 : startDay - 1; // Convert to Mon=0, Tue=1, ..., Sun=6

        const daysInMonth = currentMonth.daysInMonth();

        const days: { date: Dayjs; isCurrentMonth: boolean }[] = [];

        // Previous month days
        for (let i = startDay - 1; i >= 0; i--) {
            days.push({ date: startOfMonth.subtract(i + 1, 'day'), isCurrentMonth: false });
        }

        // Current month days
        for (let i = 1; i <= daysInMonth; i++) {
            days.push({ date: currentMonth.date(i), isCurrentMonth: true });
        }

        // Next month days to fill the grid (complete rows of 7)
        const remaining = 7 - (days.length % 7);
        if (remaining < 7) {
            for (let i = 1; i <= remaining; i++) {
                days.push({ date: endOfMonth.add(i, 'day'), isCurrentMonth: false });
            }
        }

        return days;
    }, [currentMonth]);

    // Group items by date key (YYYY-MM-DD)
    const itemsByDate = useMemo(() => {
        const map: Record<string, VehicleReservationDto[]> = {};
        items.forEach(item => {
            const start = dayjs(item.startDate);
            const end = dayjs(item.endDate);
            const monthStart = currentMonth.startOf('month');
            const monthEnd = currentMonth.endOf('month');

            // For each day the reservation spans within the visible month, add it
            let day = start.isBefore(monthStart) ? monthStart : start;
            const lastDay = end.isAfter(monthEnd) ? monthEnd : end;

            while (day.isBefore(lastDay) || day.isSame(lastDay, 'day')) {
                const key = day.format('YYYY-MM-DD');
                if (!map[key]) map[key] = [];
                map[key].push(item);
                day = day.add(1, 'day');
            }
        });
        return map;
    }, [items, currentMonth]);

    const selectedItems = useMemo(() => {
        if (!selectedDate) return [];
        const key = selectedDate.format('YYYY-MM-DD');
        return itemsByDate[key] || [];
    }, [selectedDate, itemsByDate]);

    const handleDayClick = useCallback((date: Dayjs) => {
        onSelectDate(selectedDate && selectedDate.isSame(date, 'day') ? null : date);
    }, [onSelectDate, selectedDate]);

    const today = dayjs();

    return (
        <div className={styles.container}>
            <div className={styles.calendarSection}>
                <div className={styles.weekDaysRow}>
                    {weekDays.map(day => (
                        <div key={day} className={styles.weekDayLabel}>{day}</div>
                    ))}
                </div>

                <div className={styles.daysGrid}>
                    {calendarDays.map(({ date, isCurrentMonth }) => {
                        const key = date.format('YYYY-MM-DD');
                        const dayItems = itemsByDate[key] || [];
                        const isSelected = selectedDate ? selectedDate.isSame(date, 'day') : false;
                        const isToday = today.isSame(date, 'day');

                        return (
                            <CalendarDayCell
                                key={key}
                                date={date}
                                items={dayItems}
                                mode={mode}
                                isCurrentMonth={isCurrentMonth}
                                isSelected={isSelected}
                                isToday={isToday}
                                onClick={handleDayClick}
                            />
                        );
                    })}
                </div>

                <div className={styles.legend}>
                    <span className={styles.legendLabel}>{t('reservations.calendar.legend')}:</span>
                    <span className={styles.legendItem}>
                        <span className={styles.legendSwatch} /> {t('reservations.calendar.legend_no_reservations')}
                    </span>
                    <span className={styles.legendItem}>
                        <span className={`${styles.legendSwatch} ${styles.legendSwatchActive}`} /> {t('reservations.calendar.legend_with_reservations')}
                    </span>
                    <span className={styles.legendItem}>
                        <span className={styles.legendBadge} /> {t('reservations.calendar.legend_reservations')}
                    </span>
                    {hasWorkflow && (
                        <span className={styles.legendItem}>
                            <span className={`${styles.legendBadge} ${styles.legendBadgeWarning}`} /> {t('reservations.calendar.legend_pending_requests')}
                        </span>
                    )}
                </div>
            </div>

            <div className={styles.sidePanelSection}>
                <CalendarSidePanel
                    mode={mode}
                    selectedDate={selectedDate}
                    items={selectedItems}
                />
            </div>
        </div>
    );
};

export default CalendarView;
